import React, { useEffect, useState, useMemo } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { ArrowLeft, ChevronLeft, ChevronRight, Crown, Calendar } from 'lucide-react'
import { useSelector, useDispatch } from 'react-redux'
import { RootState } from '../store'
import { useTranslation } from '../hooks/use-i18n'
import { setRoomMatches } from '../store/matchesSlice'
import matchService from '../services/matchService'
import futureChampionService from '../services/futureChampionService'
import topScorerService from '../services/topScorerService'
import roomsService from '../services/roomsService'
import { FutureChampionEvent, FutureChampionPrediction } from '../types/futureChampion'
import { TopScorerPrediction } from '../types/topScorer'
import { PredictExtraStep } from '../types/predictionSteps'
import TopScorerPredictPanel from '../components/TopScorerPredictPanel'
import TopScorerExistedModal from '../components/TopScorerExistedModal'
import { PredictionTypeEnum, ScoreEnum, CreatePredictionData, UpdatePredictionData } from '../types/prediction'
import { Prediction } from '../types/match'
import SelectResultModal from '../components/SelectResultModal'
import ExistedPredictionModal from '../components/ExistedPredictionModal'
import TeamNameWithFlag from '../components/TeamNameWithFlag'
import VictoryTeamLabel from '../components/VictoryTeamLabel'
import LeagueBadge from '../components/LeagueBadge'
import FutureChampionExistedModal from '../components/FutureChampionExistedModal'
import PlayoffOutcomeModal, { type PlayoffChoice } from '../components/PlayoffOutcomeModal'
import PredictionCopilotPanel from '../components/agent/PredictionCopilotPanel'
import PlayWorldFromRoomModal from '../components/PlayWorldFromRoomModal'
import PlayWorldRoomBanner from '../components/PlayWorldRoomBanner'
import LeaveWorldFootballModal from '../components/LeaveWorldFootballModal'
import { AgentPredictionProposal } from '../services/agentService'
import { getWorldCup2026TeamFlag } from '../constants/worldCup2026TeamFlags'
import { getMatchDate, formatDate } from '../utils/dateHelper'
import { toast } from 'react-hot-toast'

/** First leg and second leg scores are teamA-teamB. Returns true if aggregate is a draw (potential ET/penalties). */
function isAggregateDraw(firstLegScore: string, secondLegScore: string): boolean {
  const [a1, b1] = firstLegScore.split('-').map(s => parseInt(s.trim(), 10))
  const [a2, b2] = secondLegScore.split('-').map(s => parseInt(s.trim(), 10))
  if ([a1, b1, a2, b2].some(n => isNaN(n))) return false
  return a1 + a2 === b1 + b2
}

/** Returns true if score is a draw (e.g. "1-1", "0-0"). Used for playoff_unique_game: draw → ET/penalties. */
function isDrawScore(score: string): boolean {
  const parts = score.split('-').map(s => parseInt(s.trim(), 10))
  if (parts.length !== 2 || parts.some(n => isNaN(n))) return false
  return parts[0] === parts[1]
}

function normalizeChoiceDisplay(raw: string): string {
  if (typeof raw !== 'string') return String(raw)
  return raw.replace(/^[\s"{]+|[\s"}]+$/g, '').trim()
}

function canonLeagueSlug(v: string): string {
  const s = v.trim().toLowerCase()
  if (s === 'liga-1' || s === 'public-football-liga-1') return 'ligue1'
  if (s === 'la-liga' || s === 'public-football-la-liga') return 'laliga'
  return s
}

function isLeagueEnrolledInWorld(enrolled: string[], matchLeague: string): boolean {
  const c = canonLeagueSlug(matchLeague)
  return enrolled.some((l) => canonLeagueSlug(l) === c)
}

function renderFutureChampionChoice(raw: string): React.ReactNode {
  const choice = normalizeChoiceDisplay(raw)
  const flag = getWorldCup2026TeamFlag(choice)
  if (!flag) return choice
  return (
    <span className="inline-flex items-center justify-center gap-1.5">
      <span aria-hidden>{flag}</span>
      <span>{choice}</span>
    </span>
  )
}

const Predictions: React.FC = () => {
  const { roomId } = useParams<{ roomId: string }>()
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const { user } = useSelector((state: RootState) => state.auth)
  const { roomMatches } = useSelector((state: RootState) => state.matches)
  const matches = useMemo(() => roomId ? roomMatches[roomId] || [] : [], [roomId, roomMatches])
  // Only show matches whose kickoff is in the future (no predicting from kickoff)
  const upcomingMatches = useMemo(
    () => matches.filter((m) => new Date(getMatchDate(m, true)).getTime() > Date.now()),
    [matches]
  )

  const [selectedIndex, setSelectedIndex] = useState(0)
  const [selectedType, setSelectedType] = useState<PredictionTypeEnum>(PredictionTypeEnum.Win)
  const [selectedWinnerTeam, setSelectedWinnerTeam] = useState<string | null>(null)
  const [isOpenResultModal, setIsOpenResultModal] = useState(false)
  const [isEditMode, setIsEditMode] = useState(false)
  const [isAppLoading, setIsAppLoading] = useState(true)
  const [predictionsByMatchId, setPredictionsByMatchId] = useState<Record<string, Prediction>>({})
  const [isOpenPlayoffModal, setIsOpenPlayoffModal] = useState(false)
  const [pendingPrediction, setPendingPrediction] = useState<{ winner: string | null; predictedScore: string } | null>(null)
  const [playoffSubmitting, setPlayoffSubmitting] = useState(false)

  const [extraSteps, setExtraSteps] = useState<PredictExtraStep[]>([])
  const [futureChampionPredictionsMap, setFutureChampionPredictionsMap] = useState<Record<string, FutureChampionPrediction>>({})
  const [topScorerPredictionsMap, setTopScorerPredictionsMap] = useState<Record<string, TopScorerPrediction>>({})
  const [futureChampionEditMode, setFutureChampionEditMode] = useState(false)
  const [topScorerEditMode, setTopScorerEditMode] = useState(false)
  const [futureChampionSubmitting, setFutureChampionSubmitting] = useState(false)
  const [topScorerSubmitting, setTopScorerSubmitting] = useState(false)
  const [futureChampionQuery, setFutureChampionQuery] = useState('')
  const [showPlayWorldModal, setShowPlayWorldModal] = useState(false)
  const [showLeaveWorldModal, setShowLeaveWorldModal] = useState(false)

  const playerId = user?._id ?? (user as any)?.id ?? ''
  const extraStepCount = extraSteps.length
  const totalSteps = extraStepCount + upcomingMatches.length
  const isExtraStep = selectedIndex < extraStepCount
  const isMatchStep = !isExtraStep
  const currentExtraStep = isExtraStep ? extraSteps[selectedIndex] ?? null : null
  const isTopScorerStep = currentExtraStep?.stepType === 'top_scorer'
  const isFutureChampionStep = currentExtraStep?.stepType === 'future_champion'
  const currentTopScorerEvent = isTopScorerStep ? currentExtraStep.event : null
  const currentFutureChampionEvent = isFutureChampionStep ? currentExtraStep.event : null
  const currentMatch = isMatchStep ? upcomingMatches[selectedIndex - extraStepCount] ?? null : null
  const currentFutureChampionPrediction = currentFutureChampionEvent
    ? futureChampionPredictionsMap[currentFutureChampionEvent.id ?? (currentFutureChampionEvent as any)._id]
    : null
  const currentTopScorerPrediction = currentTopScorerEvent
    ? topScorerPredictionsMap[currentTopScorerEvent.id]
    : null
  const isFutureChampionEventOpen = (event: FutureChampionEvent) =>
    event?.isActive && new Date() < new Date(event.eventDate)
  const formatEventDate = (dateString: string) =>
    new Date(dateString).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })

  const filteredFutureChampionChoices = useMemo(() => {
    const q = futureChampionQuery.trim().toLowerCase()
    const list = currentFutureChampionEvent?.choices || []
    if (!q) return list
    return list.filter((c) => normalizeChoiceDisplay(c).toLowerCase().includes(q))
  }, [currentFutureChampionEvent, futureChampionQuery])

  // Derived — no useState, no async fetch, no timer. Always reflects current state.
  const existedPrediction: Prediction | null = useMemo(() => {
    if (!isMatchStep || !currentMatch || isEditMode) return null
    return predictionsByMatchId[currentMatch._id] ?? null
  }, [isMatchStep, currentMatch, isEditMode, predictionsByMatchId])

  const showTopScorerExistedModal = Boolean(
    isTopScorerStep && currentTopScorerPrediction && !topScorerEditMode
  )

  const showFutureChampionExistedModal = Boolean(
    isFutureChampionStep && currentFutureChampionPrediction && !futureChampionEditMode
  )

  const { data: matchesData, isLoading: matchesLoading, isError: matchesError } = useQuery({
    queryKey: ['room-matches', roomId],
    queryFn: () => matchService.getRoomMatches(roomId!),
    enabled: Boolean(roomId),
    staleTime: 60 * 1000,
  })

  const { data: roomSummary } = useQuery({
    queryKey: ['room-summary', roomId],
    queryFn: () => roomsService.getRoomSummary(roomId!),
    enabled: Boolean(roomId),
    staleTime: 60 * 1000,
  })

  const isFootballRoom = (roomSummary?.gameType ?? 'football') === 'football'

  const { data: publicFootballStatus, refetch: refetchPublicStatus } = useQuery({
    queryKey: ['public-football-status', roomId],
    queryFn: () => roomsService.getPublicFootballStatus(roomId!),
    enabled: Boolean(roomId) && isFootballRoom,
    staleTime: 30 * 1000,
  })

  const showPlayWorldCta = Boolean(
    isFootballRoom &&
    publicFootballStatus?.syncEnabled &&
    publicFootballStatus.roomLeagues.length > 0 &&
    !publicFootballStatus.fullyEnrolled,
  )

  const showViewWorldLink = Boolean(
    isFootballRoom &&
    publicFootballStatus?.syncEnabled &&
    publicFootballStatus.fullyEnrolled,
  )

  const showWorldSyncHint = Boolean(
    isMatchStep &&
    currentMatch &&
    publicFootballStatus?.syncEnabled &&
    isLeagueEnrolledInWorld(publicFootballStatus.enrolledLeagues ?? [], currentMatch.league),
  )

  useEffect(() => {
    if (!roomId) return
    if (matchesError) dispatch(setRoomMatches({ roomId, matches: [] }))
    else if (matchesData !== undefined) dispatch(setRoomMatches({ roomId, matches: matchesData }))
  }, [matchesData, matchesError, roomId, dispatch])

  useEffect(() => {
    setIsAppLoading(matchesLoading)
  }, [matchesLoading])

  // Reset ALL edit modes on every step change.
  useEffect(() => {
    setIsEditMode(false)
    setTopScorerEditMode(false)
    setFutureChampionEditMode(false)
  }, [selectedIndex])

  useEffect(() => { setFutureChampionQuery('') }, [selectedIndex])

  useEffect(() => {
    if (!roomId || !playerId) return
    let cancelled = false
    Promise.all([
      roomsService.getPredictionSteps(roomId),
      futureChampionService.getPlayerPredictions(roomId, playerId),
      topScorerService.getPlayerPredictions(roomId, playerId),
      matchService.getAllRoomPredictions(roomId),
    ])
      .then(([stepsApi, fcPreds, tsPreds, allPreds]) => {
        if (cancelled) return
        const steps: PredictExtraStep[] = (stepsApi || []).map((s) =>
          s.type === 'top_scorer'
            ? { stepType: 'top_scorer' as const, event: s.event }
            : { stepType: 'future_champion' as const, event: s.event },
        )
        setExtraSteps(steps)
        const fcMap: Record<string, FutureChampionPrediction> = {}
        ;(Array.isArray(fcPreds) ? fcPreds : []).forEach((p) => {
          const eid = p.eventId ?? (p.event as any)?.id
          if (eid) fcMap[eid] = p
        })
        setFutureChampionPredictionsMap(fcMap)
        const tsMap: Record<string, TopScorerPrediction> = {}
        ;(Array.isArray(tsPreds) ? tsPreds : []).forEach((p) => {
          if (p.eventId) tsMap[p.eventId] = p
        })
        setTopScorerPredictionsMap(tsMap)
        setPredictionsByMatchId(allPreds && typeof allPreds === 'object' ? allPreds : {})
      })
      .catch(() => {
        if (!cancelled) setExtraSteps([])
      })
    return () => { cancelled = true }
  }, [roomId, playerId])

  const handleOpenResultClick = (type: PredictionTypeEnum, team: string | null) => {
    setSelectedType(type)
    setIsOpenResultModal(true)
    setSelectedWinnerTeam(team)
  }

  const submitPrediction = async (
    base: { winner: string | null; predictedScore: string },
    extra: { predictedWinnerAdditionalTime?: string | null; predictedWinnerPenalties?: string | null } = {}
  ) => {
    if (!roomId || !currentMatch) return
    const matchId = currentMatch._id
    const data: CreatePredictionData | UpdatePredictionData = {
      winner: base.winner,
      predictedScore: base.predictedScore,
      ...extra,
    }
    try {
      let saved: Prediction
      if (isEditMode) {
        saved = await matchService.updatePrediction(roomId, matchId, data)
      } else {
        saved = await matchService.createPrediction({ ...data, roomId, matchId })
      }
      if (saved) {
        setPredictionsByMatchId((prev) => ({
          ...prev,
          [matchId]: {
            ...saved,
            predictedWinnerAdditionalTime:
              (saved as any).predictedWinnerAdditionalTime ?? extra.predictedWinnerAdditionalTime ?? null,
            predictedWinnerPenalties:
              (saved as any).predictedWinnerPenalties ?? extra.predictedWinnerPenalties ?? null,
          },
        }))
      }
      setSelectedIndex((prev) => prev + 1)
      setIsOpenResultModal(false)
      setIsOpenPlayoffModal(false)
      setPendingPrediction(null)
      setIsEditMode(false)
    } catch (error: any) {
      console.error('Failed to save prediction:', error)
      toast.error(error?.response?.data?.message || 'Failed to save prediction')
      throw error
    }
  }

  const handleResultSelect = async (score: ScoreEnum) => {
    if (!roomId || !currentMatch) return

    let scoreToSend = score
    if (selectedWinnerTeam === currentMatch.teamB && score !== ScoreEnum.FiveAndMore && score !== ScoreEnum.FiveFiveMore) {
      const [scoreA, scoreB] = score.split('-')
      scoreToSend = `${scoreB}-${scoreA}` as ScoreEnum
    }

    const base = { winner: selectedWinnerTeam, predictedScore: scoreToSend }

    // Second leg only: ET/pen when aggregate tied. First leg has no firstLegMatchScore → no modal.
    const hasFirstLeg = currentMatch.firstLegMatchScore && currentMatch.firstLegMatchScore.trim().length > 0
    const twoLegPlayoffDraw =
      currentMatch.playoff && hasFirstLeg && isAggregateDraw(currentMatch.firstLegMatchScore!.trim(), scoreToSend)
    const singleGamePlayoffDraw = (currentMatch as any).playoffUniqueGame && isDrawScore(scoreToSend)
    const potentialPlayoff = twoLegPlayoffDraw || singleGamePlayoffDraw

    if (potentialPlayoff) {
      setPendingPrediction(base)
      setIsOpenResultModal(false)
      setIsOpenPlayoffModal(true)
      return
    }

    await submitPrediction(base, {})
  }

  const handleApplyCopilotProposal = async (proposal: AgentPredictionProposal) => {
    if (!roomId || !currentMatch) return

    let scoreToSend = proposal.predictedScore as ScoreEnum
    if (
      proposal.winner === currentMatch.teamB &&
      scoreToSend !== ScoreEnum.FiveAndMore &&
      scoreToSend !== ScoreEnum.FiveFiveMore
    ) {
      const [scoreA, scoreB] = scoreToSend.split('-')
      scoreToSend = `${scoreB}-${scoreA}` as ScoreEnum
    }

    const base = { winner: proposal.winner, predictedScore: scoreToSend }
    const hasFirstLeg = currentMatch.firstLegMatchScore && currentMatch.firstLegMatchScore.trim().length > 0
    const twoLegPlayoffDraw =
      currentMatch.playoff && hasFirstLeg && isAggregateDraw(currentMatch.firstLegMatchScore!.trim(), scoreToSend)
    const singleGamePlayoffDraw = (currentMatch as any).playoffUniqueGame && isDrawScore(scoreToSend)
    const potentialPlayoff = twoLegPlayoffDraw || singleGamePlayoffDraw

    if (potentialPlayoff) {
      setPendingPrediction(base)
      setIsOpenPlayoffModal(true)
      return
    }

    await submitPrediction(base, {})
  }

  const handlePlayoffChoice = async (choice: PlayoffChoice) => {
    if (!pendingPrediction || playoffSubmitting) return
    setPlayoffSubmitting(true)
    try {
      if (choice.type === 'additional_time') {
        await submitPrediction(pendingPrediction, {
          predictedWinnerAdditionalTime: choice.teamName,
          predictedWinnerPenalties: null,
        })
      } else {
        await submitPrediction(pendingPrediction, {
          predictedWinnerAdditionalTime: null,
          predictedWinnerPenalties: choice.teamName,
        })
      }
    } catch {
      // keep modal open so user can retry; toast already shown
    } finally {
      setPlayoffSubmitting(false)
    }
  }

  const handleKeepClick = () => {
    setSelectedIndex(prev => prev + 1)
  }

  const handleChangeClick = () => {
    setIsEditMode(true)
  }

  const handleFutureChampionChoiceClick = async (choice: string) => {
    if (!roomId || !currentFutureChampionEvent || futureChampionSubmitting) return
    const eid = currentFutureChampionEvent.id ?? (currentFutureChampionEvent as any)._id
    if (currentFutureChampionPrediction && !futureChampionEditMode) return

    try {
      setFutureChampionSubmitting(true)
      const prediction = await futureChampionService.createPrediction({
        roomId,
        eventId: eid,
        predictedChampion: choice,
      })
      setFutureChampionPredictionsMap((prev) => ({ ...prev, [eid]: prediction }))
      toast.success(t('futureChampion.saved') || 'Prediction saved')
      setFutureChampionEditMode(false)
      setSelectedIndex((i) => i + 1)
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Failed to save')
    } finally {
      setFutureChampionSubmitting(false)
    }
  }

  const handleTopScorerChoiceClick = async (playerName: string) => {
    if (!roomId || !currentTopScorerEvent || topScorerSubmitting) return
    const eid = currentTopScorerEvent.id
    if (currentTopScorerPrediction && !topScorerEditMode) return

    try {
      setTopScorerSubmitting(true)
      const prediction = await topScorerService.createPrediction({
        roomId,
        eventId: eid,
        predictedScorer: playerName,
      })
      setTopScorerPredictionsMap((prev) => ({ ...prev, [eid]: prediction }))
      toast.success(t('topScorer.saved'))
      setTopScorerEditMode(false)
      setSelectedIndex((i) => i + 1)
    } catch (err: any) {
      toast.error(err.response?.data?.message || t('topScorer.saveFailed'))
    } finally {
      setTopScorerSubmitting(false)
    }
  }

  const handleFutureChampionKeep = () => {
    setSelectedIndex((i) => i + 1)
  }

  const handleFutureChampionChange = () => {
    setFutureChampionEditMode(true)
  }

  const handleTopScorerKeep = () => {
    setSelectedIndex((i) => i + 1)
  }

  const handleTopScorerChange = () => {
    setTopScorerEditMode(true)
  }

  if (isAppLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-sky-500"></div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-background p-4 sm:p-6">
      <div className="max-w-2xl mx-auto">
        {/* Back to Room - breakroom-champions style */}
        <Link
          to={`/room/${roomId}`}
          className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors mb-4 sm:mb-6 text-small sm:text-body"
        >
          <ArrowLeft className="w-4 h-4 shrink-0" />
          {t('predictions.backToRoom')}
        </Link>

        {(showPlayWorldCta || showViewWorldLink) && (
          <PlayWorldRoomBanner
            className="mb-3"
            showPlayWorldCta={showPlayWorldCta}
            showViewWorldLink={showViewWorldLink}
            onOpenModal={() => setShowPlayWorldModal(true)}
            onLeaveWorld={() => setShowLeaveWorldModal(true)}
          />
        )}

        {showLeaveWorldModal && roomId && (
          <LeaveWorldFootballModal
            roomId={roomId}
            onClose={() => setShowLeaveWorldModal(false)}
            onSuccess={() => refetchPublicStatus()}
          />
        )}

        {showPlayWorldModal && publicFootballStatus && roomId && (
          <PlayWorldFromRoomModal
            roomId={roomId}
            status={publicFootballStatus}
            onClose={() => setShowPlayWorldModal(false)}
            onSuccess={() => refetchPublicStatus()}
          />
        )}

        <div className="blue-sky-card rounded-2xl p-6 sm:p-8 text-center">
          {totalSteps > 0 && selectedIndex >= totalSteps ? (
            <>
              <div className="text-6xl mb-6">🎉</div>
              <h2 className="text-xl sm:text-2xl font-bold text-white mb-4">
                {t('predictions.completedAll')}
              </h2>
              <button
                onClick={() => navigate(`/room/${roomId}`)}
                className="px-6 sm:px-8 py-2.5 sm:py-3 bg-primary hover:opacity-90 text-primary-foreground rounded-xl font-semibold transition-opacity text-small sm:text-nav"
              >
                {t('predictions.backToRoomButton')}
              </button>
            </>
          ) : (
            <>
              {totalSteps === 0 ? (
                <>
                  <div className="text-6xl mb-6">⚽</div>
                  <h2 className="text-xl sm:text-2xl font-bold text-white mb-4">
                    {t('predictions.noGames')}
                  </h2>
                  <p className="text-white/80 text-small sm:text-body mb-6">
                    {t('predictions.noGamesNotify')}
                  </p>
                  <Link
                    to={`/room/${roomId}`}
                    className="inline-block px-6 sm:px-8 py-2.5 sm:py-3 bg-primary hover:opacity-90 text-primary-foreground rounded-xl font-semibold transition-opacity text-small sm:text-nav"
                  >
                    {t('predictions.backToRoomButton')}
                  </Link>
                </>
              ) : isMatchStep && currentMatch ? (
                <>
                  {/* Progress indicator - unified (matches + future champion steps) */}
                  <div className="flex items-center justify-between w-full mb-6">
                    <div className="flex gap-1 sm:gap-1.5">
                      {Array.from({ length: Math.min(totalSteps, 12) }).map((_, i) => (
                        <div
                          key={i}
                          className={`h-2 w-4 sm:w-5 rounded-sm flex-shrink-0 ${
                            i <= selectedIndex ? 'bg-primary' : 'bg-white/20'
                          }`}
                        />
                      ))}
                    </div>
                    <span className="text-white/80 font-semibold text-small sm:text-body ml-2">
                      {selectedIndex + 1}/{totalSteps}
                    </span>
                  </div>

                  {/* League badge + match number (e.g. Day 11) */}
                  <div className="mb-4">
                    <div className="mb-2 flex justify-center">
                      <LeagueBadge league={currentMatch.league} size={52} />
                    </div>
                    <h1 className="text-xl sm:text-2xl font-bold text-white">{currentMatch?.number ?? t('predictions.roomPredictions')}</h1>
                  </div>
                  <div className="flex flex-wrap items-center justify-center gap-2 mb-6 sm:mb-8">
                    <p className="text-white/80 text-small sm:text-body">
                      {formatDate(getMatchDate(currentMatch, true), true)}
                    </p>
                    {showWorldSyncHint && (
                      <span className="px-2.5 py-1 rounded-full text-xs font-medium text-emerald-100 bg-emerald-600/40">
                        {t('prediction.syncedWithWorld')}
                      </span>
                    )}
                    {(currentMatch.playoff || (currentMatch as any).playoffUniqueGame) && (
                      <>
                        {currentMatch.playoff && (
                          <span className="px-2.5 py-1 rounded-full text-xs font-medium text-white/90 bg-white/20">
                            1st leg: {currentMatch.firstLegMatchScore?.trim() || '—'}
                          </span>
                        )}
                        <span className="px-2.5 py-1 rounded-full text-xs font-medium text-white bg-amber-500/90">
                          {(currentMatch as any).playoffUniqueGame && !currentMatch.playoff ? 'Single-game playoff' : 'Playoff'}
                        </span>
                      </>
                    )}
                  </div>

                  {/* Teams - icon above name, VS in center (breakroom-champions style) */}
                  <div className="flex justify-between items-center mb-6 sm:mb-8 gap-4">
                    <div className="flex-1 text-center min-w-0">
                      <div className="w-12 h-12 sm:w-16 sm:h-16 bg-white/10 rounded-full flex items-center justify-center mx-auto mb-2">
                        <span className="text-xl sm:text-2xl">🏠</span>
                      </div>
                      <h3 className="text-base sm:text-lg font-semibold text-white">
                        <TeamNameWithFlag
                          teamName={currentMatch.teamA}
                          league={currentMatch.league}
                          side="home"
                          nameClassName="text-base sm:text-lg font-semibold text-white"
                        />
                      </h3>
                    </div>
                    <div className="text-xl sm:text-2xl font-bold text-white flex-shrink-0">{t('predictions.vs')}</div>
                    <div className="flex-1 text-center min-w-0">
                      <div className="w-12 h-12 sm:w-16 sm:h-16 bg-white/10 rounded-full flex items-center justify-center mx-auto mb-2">
                        <span className="text-xl sm:text-2xl">✈️</span>
                      </div>
                      <h3 className="text-base sm:text-lg font-semibold text-white">
                        <TeamNameWithFlag
                          teamName={currentMatch.teamB}
                          league={currentMatch.league}
                          side="away"
                          nameClassName="text-base sm:text-lg font-semibold text-white"
                        />
                      </h3>
                    </div>
                  </div>

                  <PredictionCopilotPanel
                    roomId={roomId!}
                    matchId={currentMatch._id}
                    teamA={currentMatch.teamA}
                    teamB={currentMatch.teamB}
                    onApplyProposal={handleApplyCopilotProposal}
                  />

                  {/* Prediction Buttons */}
                  <div className="space-y-3 sm:space-y-4">
                    <button
                      onClick={() => handleOpenResultClick(PredictionTypeEnum.Win, currentMatch.teamA)}
                      className="w-full px-6 py-3 sm:py-4 bg-primary hover:opacity-90 text-primary-foreground rounded-xl font-semibold transition-opacity text-small sm:text-body"
                    >
                      <VictoryTeamLabel
                        teamName={currentMatch.teamA}
                        league={currentMatch.league}
                        side="home"
                        nameClassName="text-small sm:text-body"
                      />
                    </button>
                    <button
                      onClick={() => handleOpenResultClick(PredictionTypeEnum.Draw, null)}
                      className="w-full px-6 py-3 sm:py-4 bg-white/10 hover:bg-white/20 text-white rounded-xl font-semibold transition-all text-small sm:text-body"
                    >
                      {t('predictions.draw')}
                    </button>
                    <button
                      onClick={() => handleOpenResultClick(PredictionTypeEnum.Win, currentMatch.teamB)}
                      className="w-full px-6 py-3 sm:py-4 bg-primary hover:opacity-90 text-primary-foreground rounded-xl font-semibold transition-opacity text-small sm:text-body"
                    >
                      <VictoryTeamLabel
                        teamName={currentMatch.teamB}
                        league={currentMatch.league}
                        side="away"
                        nameClassName="text-small sm:text-body"
                      />
                    </button>
                  </div>

                  {/* Card nav arrows - breakroom-champions style */}
                  <div className="flex items-center justify-center gap-4 mt-6 sm:mt-8">
                    <button
                      type="button"
                      onClick={() => setSelectedIndex((i) => Math.max(0, i - 1))}
                      disabled={selectedIndex === 0}
                      className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 disabled:opacity-40 disabled:pointer-events-none flex items-center justify-center text-white transition-colors"
                      aria-label="Previous"
                    >
                      <ChevronLeft className="w-5 h-5" />
                    </button>
                    <button
                      type="button"
                      onClick={() => setSelectedIndex((i) => Math.min(totalSteps - 1, i + 1))}
                      disabled={selectedIndex >= totalSteps - 1}
                      className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 disabled:opacity-40 disabled:pointer-events-none flex items-center justify-center text-white transition-colors"
                      aria-label="Next"
                    >
                      <ChevronRight className="w-5 h-5" />
                    </button>
                  </div>

                  <SelectResultModal
                    isOpen={isOpenResultModal}
                    title={`${currentMatch.teamA} - ${currentMatch.teamB}`}
                    type={selectedType}
                    variant={selectedWinnerTeam === currentMatch.teamA ? 'a' : 'b'}
                    onClose={() => setIsOpenResultModal(false)}
                    onSelect={handleResultSelect}
                  />

                  <ExistedPredictionModal
                    existedPrediction={existedPrediction}
                    match={currentMatch}
                    onChangeClick={handleChangeClick}
                    onKeepClick={handleKeepClick}
                  />

                  <PlayoffOutcomeModal
                    isOpen={isOpenPlayoffModal}
                    teamA={currentMatch.teamA}
                    teamB={currentMatch.teamB}
                    submitting={playoffSubmitting}
                    onSelect={handlePlayoffChoice}
                    onClose={() => {
                      if (playoffSubmitting) return
                      setIsOpenPlayoffModal(false)
                      setPendingPrediction(null)
                    }}
                  />
                </>
              ) : isTopScorerStep && currentTopScorerEvent ? (
                <>
                  <div className="flex items-center justify-between w-full mb-6">
                    <div className="flex gap-1 sm:gap-1.5">
                      {Array.from({ length: Math.min(totalSteps, 12) }).map((_, i) => (
                        <div
                          key={i}
                          className={`h-2 w-4 sm:w-5 rounded-sm flex-shrink-0 ${
                            i <= selectedIndex ? 'bg-primary' : 'bg-white/20'
                          }`}
                        />
                      ))}
                    </div>
                    <span className="text-white/80 font-semibold text-small sm:text-body ml-2">
                      {selectedIndex + 1}/{totalSteps}
                    </span>
                  </div>
                  <TopScorerPredictPanel
                    event={currentTopScorerEvent}
                    submitting={topScorerSubmitting}
                    onSelect={handleTopScorerChoiceClick}
                    formatEventDate={formatEventDate}
                  />
                  <div className="flex items-center justify-center gap-4 mt-6 sm:mt-8">
                    <button
                      type="button"
                      onClick={() => setSelectedIndex((i) => Math.max(0, i - 1))}
                      disabled={selectedIndex === 0}
                      className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 disabled:opacity-40 disabled:pointer-events-none flex items-center justify-center text-white transition-colors"
                      aria-label="Previous"
                    >
                      <ChevronLeft className="w-5 h-5" />
                    </button>
                    <button
                      type="button"
                      onClick={() => setSelectedIndex((i) => Math.min(totalSteps - 1, i + 1))}
                      disabled={selectedIndex >= totalSteps - 1}
                      className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 disabled:opacity-40 disabled:pointer-events-none flex items-center justify-center text-white transition-colors"
                      aria-label="Next"
                    >
                      <ChevronRight className="w-5 h-5" />
                    </button>
                  </div>
                  {showTopScorerExistedModal && currentTopScorerPrediction && (
                    <TopScorerExistedModal
                      eventName={currentTopScorerEvent.name}
                      currentChoice={currentTopScorerPrediction.predictedScorer}
                      onKeepClick={handleTopScorerKeep}
                      onChangeClick={handleTopScorerChange}
                    />
                  )}
                </>
              ) : isFutureChampionStep && currentFutureChampionEvent ? (
                <>
                  <div className="flex items-center justify-between w-full mb-6">
                    <div className="flex gap-1 sm:gap-1.5">
                      {Array.from({ length: Math.min(totalSteps, 12) }).map((_, i) => (
                        <div
                          key={i}
                          className={`h-2 w-4 sm:w-5 rounded-sm flex-shrink-0 ${
                            i <= selectedIndex ? 'bg-primary' : 'bg-white/20'
                          }`}
                        />
                      ))}
                    </div>
                    <span className="text-white/80 font-semibold text-small sm:text-body ml-2">
                      {selectedIndex + 1}/{totalSteps}
                    </span>
                  </div>
                  <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-white/10 mb-3">
                    <Crown className="w-7 h-7 text-yellow-400" />
                  </div>
                  <h1 className="text-xl sm:text-2xl font-bold text-white mb-1">
                    {t('futureChampion.title') || 'Future Champion'}
                  </h1>
                  <h2 className="text-lg font-semibold text-white/90 mb-2">{currentFutureChampionEvent.name}</h2>
                  <div className="flex items-center justify-center gap-2 text-white/80 text-sm mb-6">
                    <Calendar className="w-4 h-4" />
                    <span>Deadline: {formatEventDate(currentFutureChampionEvent.eventDate)}</span>
                  </div>
                  {!isFutureChampionEventOpen(currentFutureChampionEvent) ? (
                    <p className="text-white/80">
                      {t('futureChampion.closed') || 'Predictions closed for this event.'}
                    </p>
                  ) : (
                    <>
                      <p className="text-white/90 text-sm sm:text-body mb-3">
                        {t('futureChampion.selectChampion') || 'Select your predicted champion:'}
                      </p>
                      <input
                        type="search"
                        value={futureChampionQuery}
                        onChange={(e) => setFutureChampionQuery(e.target.value)}
                        placeholder={t('futureChampion.searchPlaceholder')}
                        className="w-full mb-2 px-4 py-2.5 rounded-xl bg-white/10 border border-white/20 text-white placeholder:text-white/50 text-sm focus:outline-none focus:border-primary"
                      />
                      <p className="text-white/60 text-xs mb-3 text-left">{t('futureChampion.searchHint')}</p>
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 sm:gap-3">
                        {filteredFutureChampionChoices.map((choice) => (
                          <button
                            key={String(choice)}
                            onClick={() => handleFutureChampionChoiceClick(choice)}
                            disabled={futureChampionSubmitting}
                            className="px-4 py-3 sm:py-4 bg-primary hover:opacity-90 disabled:opacity-50 text-primary-foreground rounded-xl font-semibold text-sm sm:text-body transition-opacity"
                          >
                            {renderFutureChampionChoice(choice)}
                          </button>
                        ))}
                        {filteredFutureChampionChoices.length === 0 && (
                          <p className="col-span-2 sm:col-span-3 text-white/70 text-sm text-center py-4">
                            {t('topScorer.noResults')}
                          </p>
                        )}
                      </div>
                    </>
                  )}
                  <div className="flex items-center justify-center gap-4 mt-6 sm:mt-8">
                    <button
                      type="button"
                      onClick={() => setSelectedIndex((i) => Math.max(0, i - 1))}
                      disabled={selectedIndex === 0}
                      className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 disabled:opacity-40 disabled:pointer-events-none flex items-center justify-center text-white transition-colors"
                      aria-label="Previous"
                    >
                      <ChevronLeft className="w-5 h-5" />
                    </button>
                    <button
                      type="button"
                      onClick={() => setSelectedIndex((i) => Math.min(totalSteps - 1, i + 1))}
                      disabled={selectedIndex >= totalSteps - 1}
                      className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 disabled:opacity-40 disabled:pointer-events-none flex items-center justify-center text-white transition-colors"
                      aria-label="Next"
                    >
                      <ChevronRight className="w-5 h-5" />
                    </button>
                  </div>
                  {showFutureChampionExistedModal && currentFutureChampionPrediction && (
                    <FutureChampionExistedModal
                      eventName={currentFutureChampionEvent.name}
                      currentChoice={normalizeChoiceDisplay(currentFutureChampionPrediction.predictedChampion)}
                      onKeepClick={handleFutureChampionKeep}
                      onChangeClick={handleFutureChampionChange}
                    />
                  )}
                </>
              ) : null}
            </>
          )}
        </div>
      </div>
    </div>
  )
}

export default Predictions
